import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type { CarouselConfig } from '@segmently/schema-models/v2';
import type { PreviewMode } from '../../../types/previewMode';
import type { TemplateVariables } from '../../../utils/contentResolver';
import { mapEdgeInsetsToCSS, mapFrameLayoutStyles } from '../../../utils/styleMapper';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import { normalizeSlideItems } from '../../../utils/normalizeCollections';
import { SliderItem } from '../SliderItem';
import styles from './Slider.module.css';

export interface SliderProps {
  carousel: CarouselConfig | undefined;
  locale: string;
  currentIndex?: number;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  previewMode?: PreviewMode;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
}

export const Slider = memo(function Slider({
  carousel,
  locale,
  currentIndex = 0,
  className,
  'data-testid': testId,
  editPath,
  previewMode,
  variables,
  resolvedScreenValues,
}: SliderProps): React.ReactElement | null {
  if (!carousel) {
    return null;
  }

  const { kind, appearance, layout } = carousel;
  const items = normalizeSlideItems((carousel as any).items);

  if (!items || items.length === 0) {
    return null;
  }

  const safeIndex = Math.max(0, Math.min(currentIndex, items.length - 1));

  const containerStyle: CSSProperties = {
    ...mapFrameLayoutStyles(layout),
    ...mapEdgeInsetsToCSS(appearance?.insets),
  };

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  return (
    <div
      className={className ? `${styles.slider} ${className}` : styles.slider}
      style={containerStyle}
      data-testid={schemaTestId}
      data-edit-path={editPath}
    >
      {items.map((slide, index) => (
        <SliderItem
          key={index}
          slide={slide}
          locale={locale}
          isActive={index === safeIndex}
          carouselKind={kind}
          imageContainer={carousel.imageContainer}
          textContainer={carousel.textContainer}
          previewMode={previewMode}
          variables={variables}
          resolvedScreenValues={resolvedScreenValues}
          editPath={editPath ? `${editPath}.items[${index}]` : undefined}
        />
      ))}
    </div>
  );
});

Slider.displayName = 'Slider';
